/**
 * Shared SWG Structures types (P14.1) — the client contract.
 *
 * The client RENDERS these replicated rows (lot budget, deeds, deployed
 * structures, decorations, access lists) and CALLS the reducers; every validity,
 * lot, maintenance, decay, and redeed decision is server-authoritative. Mirrors
 * the server `structures` tables. Civic types (city_hall, …) are placed via the
 * P14.2 city-zoning path, so they are excluded from the open deed vocabulary.
 */

import type { IdentityHex } from './identity.js';

/** The deed types a player may freely mint + deploy onto the Commons. */
export const STRUCTURE_TYPES = [
  'merchant_tent',
  'small_house',
  'medium_house',
  'large_house',
  'guild_hall',
  'factory',
  'extractor_small',
  'extractor_medium',
  'extractor_large',
  'harvester',
] as const;
export type StructureType = (typeof STRUCTURE_TYPES)[number];

/** Per-structure access levels. */
export type StructurePermLevel = 'owner' | 'admin' | 'entry' | 'banned';

/** A player's lot budget. Mirrors `lot_budget`. */
export interface LotBudget {
  identity: IdentityHex;
  lotsTotal: number;
  lotsUsed: number;
}

/** A deed / packed control-device in inventory. Mirrors `structure_deed`. */
export interface StructureDeed {
  deedId: bigint;
  owner: IdentityHex;
  structureType: string;
  lotsRequired: number;
  baseMaintPerHour: number;
  itemCap: number;
  isCivic: boolean;
  packed: boolean;
  storedMaint: bigint;
  noTrade: boolean;
}

/** A deployed structure in the Commons. Mirrors `structure`. */
export interface DeployedStructure {
  structureId: bigint;
  worldId: bigint;
  owner: IdentityHex;
  structureType: string;
  cellX: number;
  cellZ: number;
  posX: number;
  posZ: number;
  yaw: number;
  lotsUsed: number;
  maintPool: bigint;
  condition: number;
  itemCap: number;
  itemCount: number;
  cityId: bigint;
  outsideCity: boolean;
}

// ─── Reducer argument types ──────────────────────────────────────────────────

export interface GrantDeedArgs {
  structureType: string;
}
export interface DeployStructureArgs {
  deedId: bigint;
  posX: number;
  posZ: number;
  yaw: number;
}
export interface PayMaintenanceArgs {
  structureId: bigint;
  amount: bigint;
}
export interface RedeedStructureArgs {
  structureId: bigint;
  confirm: boolean;
}
